import { BooleanButtonsInput, BooleanField, BooleanFieldInputProps, NumberField, TextField } from "@hex-insights/forms";
import { DiscountFormValues } from "../../FormUtilities";
import { BaseFieldProps } from "../shared";

/**
 * Generic props for fields of the Discount model.
 */
type FieldProps<K extends keyof DiscountFormValues.Base = keyof DiscountFormValues.Base> = BaseFieldProps<
	Pick<DiscountFormValues.Base, K>
>;

/**
 * Generic props for fields of the Discount model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof DiscountFormValues.Detail = keyof DiscountFormValues.Detail> = BaseFieldProps<
	Pick<DiscountFormValues.Detail, K>
>;

/**
 * Field component for the `name` field of the Discount model.
 */
export function Name({ formState, ...props }: FieldProps<"name">) {
	return <TextField formState={formState} name="name" {...props} />;
}

/**
 * Field component for the `description` field of the Discount model.
 */
export function Description({ formState, ...props }: FieldProps<"description">) {
	return <TextField formState={formState} name="description" multiLine optional {...props} />;
}

/**
 * Field component for the `isActive` field of the Discount model.
 */
export function IsActive({ formState, ...props }: DetailFieldProps<"isActive">) {
	return <BooleanField formState={formState} name="isActive" {...props} />;
}

/**
 * Field component for the `isPercentageAmount` field of the Discount model.
 */
export function IsPercentageAmount({ formState, label = "Type", ...props }: FieldProps<"isPercentageAmount">) {
	return (
		<BooleanField
			formState={formState}
			name="isPercentageAmount"
			label={label}
			Input={IsPercentageAmountInput}
			oneLine={false}
			format={formatIsPercentageAmount}
			{...props}
		/>
	);
}

function IsPercentageAmountInput(props: BooleanFieldInputProps) {
	return <BooleanButtonsInput {...props} trueLabel="Percentage-Based" falseLabel="Flat Amount" />;
}

function formatIsPercentageAmount(value: boolean) {
	return value ? "Percentage-Based" : "Flat Amount";
}

/**
 * Field component for the `flatAmount` field of the Discount model.
 */
export function FlatAmount({ formState, label = "Amount", ...props }: FieldProps<"flatAmount">) {
	return (
		<NumberField
			formState={formState}
			name="flatAmount"
			label={label}
			validationUnit={1}
			min={0}
			unit={100}
			{...props}
		/>
	);
}

/**
 * Field component for the `percentageAmount` field of the Discount model.
 */
export function PercentageAmount({ formState, label = "Percentage", ...props }: FieldProps<"percentageAmount">) {
	return <NumberField formState={formState} name="percentageAmount" label={label} min={0} {...props} />;
}
